import { Component, computed, effect, inject, signal } from '@angular/core';
import { NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';

import { NzAvatarModule } from 'ng-zorro-antd/avatar';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDropdownModule } from 'ng-zorro-antd/dropdown';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzMenuModule } from 'ng-zorro-antd/menu';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';

import { AuthService } from '@services/auth.service';
import { ThemeService } from '@services/theme.service';
import { NotificationService } from '@services/notification.service';
import { NAV_SECTIONS, NavItem, NavSection } from '@common/constants';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NotificationPanelComponent } from '@components/notification-panel/notification-panel.component';

const SIDEBAR_COLLAPSED_KEY = 'ptp-sidebar-collapsed';
const UNREAD_POLL_INTERVAL_MS = 30_000;

@Component({
  selector: 'app-layout',
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    NzAvatarModule,
    NzBadgeModule,
    NzButtonModule,
    NzDropdownModule,
    NzIconModule,
    NzMenuModule,
    NzTooltipModule,
    NotificationPanelComponent,
  ],
  templateUrl: './layout.component.html',
  styleUrl: './layout.component.scss',
})
export class LayoutComponent {
  protected authService = inject(AuthService);
  protected theme = inject(ThemeService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  /** icon-rail state — persisted the same way ThemeService persists theme mode */
  protected readonly collapsed = signal<boolean>(this.initialCollapsed());

  /** the toggle button's label — what clicking it does, not the current state */
  protected readonly collapseLabel = computed(() =>
    this.collapsed() ? 'Expand sidebar' : 'Collapse sidebar',
  );

  /** whether the bell's dropdown is open — gates the panel's own fetch/poll */
  protected readonly notifOpen = signal(false);
  protected readonly unreadCount = signal(0);

  constructor() {
    effect(() => {
      const c = this.collapsed();
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, c ? '1' : '0');
      } catch {
        /* private mode — the in-memory signal still drives the UI */
      }
    });

    // Polls app-wide (not just while the panel is open) so the badge reflects
    // a new notification without the user having to open the bell first.
    effect((onCleanup) => {
      this.refreshUnreadCount();
      const handle = setInterval(() => this.refreshUnreadCount(), UNREAD_POLL_INTERVAL_MS);
      onCleanup(() => clearInterval(handle));
    });
  }

  protected onNotifVisibleChange(visible: boolean): void {
    this.notifOpen.set(visible);
  }

  protected refreshUnreadCount(): void {
    this.notificationService.getUnreadCount().subscribe((count) => this.unreadCount.set(count));
  }

  protected readonly sections: NavSection[] = NAV_SECTIONS;

  private readonly url = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  protected readonly user = computed(() => this.authService.user());

  protected readonly fullName = computed(() => {
    const u = this.user();
    return u ? `${u.firstName} ${u.lastName}`.trim() : '';
  });

  protected readonly pageTitle = computed(() => {
    // Query string and fragment are not part of the route: a page that keeps its
    // filters in the URL (e.g. /audit-trail?action=login) must keep its title.
    const u = this.url().split(/[?#]/)[0];
    const match = this.sections
      .flatMap((s) => s.items)
      .filter((it) => it.path && (u === `/${it.path}` || u.startsWith(`/${it.path}/`)))
      .sort((a, b) => (b.path?.length ?? 0) - (a.path?.length ?? 0))[0];
    return match?.label ?? 'PTP Fleet';
  });

  protected canSee(item: NavItem): boolean {
    if (item.soon) {
      return true;
    }
    if (item.anyPermission) {
      return item.anyPermission.some((p) => this.authService.hasPermission(p));
    }
    return !item.permission || this.authService.hasPermission(item.permission);
  }

  protected sectionVisible(section: NavSection): boolean {
    return section.items.some((it) => this.canSee(it));
  }

  protected getInitialName(): string {
    const u = this.authService.user();
    return `${u?.firstName?.charAt(0) ?? ''}${u?.lastName?.charAt(0) ?? ''}`;
  }

  protected logout(): void {
    this.authService.logout();
  }

  protected toggleCollapsed(): void {
    this.collapsed.update((c) => !c);
  }

  private initialCollapsed(): boolean {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  }
}
